import React, { Component } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FETCH_GET } from "../../config";

import PageTitle from "../../components/shared/PageTitle";
import CategoriesWidget from "../../components/shared/CategoriesWidget";
import CartWidget from "../../components/shared/CartWidget";
import Loading from "../../components/shared/Loading";
import VibrantProductCard from "../../components/VibrantProductCard";
import SEO from "../../components/shared/SEO";
import { generatePageTitle, generateMetaDescription, generateKeywords } from "../../helpers/seo";

class DealProducts extends Component {
  constructor(props) {
    super(props);
    this.state = {
      products: [],
      loading: true,
      error: null,
      searchTerm: "",
      sortBy: "default",
    };
  }

  componentDidMount() {
    this.fetchDealProducts();
  }

  fetchDealProducts = async () => {
    try {
      const query = `query={dealProducts{id,name,slug,image,price,sale_price,stock,stock_status,featured,dealEndDate,dealOrder,isDeal,isNewProduct,lowStockThreshold,categories{id,title,slug},brand{id,name}}}`;
      const result = await FETCH_GET(query);

      if (result.data && result.data.data && result.data.data.dealProducts) {
        const sorted = (result.data.data.dealProducts || [])
          .slice()
          .sort((a, b) => {
            const av =
              typeof a.dealOrder === "number"
                ? a.dealOrder
                : Number.POSITIVE_INFINITY;
            const bv =
              typeof b.dealOrder === "number"
                ? b.dealOrder
                : Number.POSITIVE_INFINITY;
            if (av === bv) return 0;
            return av < bv ? -1 : 1;
          });

        this.setState({
          products: sorted,
          loading: false,
        });
      } else {
        this.setState({ loading: false });
      }
    } catch (error) {
      console.error("Deal products fetch error:", error);
      this.setState({ loading: false, error: error.message });
    }
  };

  getFilteredAndSortedProducts = () => {
    const { products, searchTerm, sortBy } = this.state;

    if (!products || products.length === 0) {
      return [];
    }

    let filtered = [...products];

    if (searchTerm) {
      filtered = filtered.filter(
        (product) =>
          product.name &&
          product.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    switch (sortBy) {
      case "featured":
        filtered = filtered.filter((product) => product.featured);
        break;
      case "deals":
        // Already all deals, no additional filter needed
        break;
      case "new":
        filtered = filtered.filter((product) => {
          if (product.isNewProduct) return true;
          if (product.created_at) {
            const now = new Date();
            const createdDate = new Date(product.created_at);
            const daysDifference = (now - createdDate) / (1000 * 60 * 60 * 24);
            return daysDifference <= 7;
          }
          return false;
        });
        break;
      case "discounted":
        filtered = filtered.filter(
          (product) => product.sale_price && product.sale_price < product.price
        );
        break;
      case "price_asc":
        filtered.sort((a, b) => {
          const priceA = a.sale_price || a.price || 0;
          const priceB = b.sale_price || b.price || 0;
          return priceA - priceB;
        });
        break;
      case "price_desc":
        filtered.sort((a, b) => {
          const priceA = a.sale_price || a.price || 0;
          const priceB = b.sale_price || b.price || 0;
          return priceB - priceA;
        });
        break;
      case "newest":
        filtered.sort((a, b) => {
          const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
          const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
          return dateB - dateA;
        });
        break;
      default:
        // Keep deal order sorting
        break;
    }

    return filtered;
  };

  handleSearchChange = (e) => {
    this.setState({ searchTerm: e.target.value });
  };

  handleSortChange = (sortBy) => {
    this.setState({ sortBy });
  };

  getButtonStyle = (activeSort) => ({
    backgroundColor: this.state.sortBy === activeSort ? "#88c000" : "white",
    color: this.state.sortBy === activeSort ? "white" : "#666",
    border:
      this.state.sortBy === activeSort ? "1px solid #88c000" : "1px solid #ddd",
    borderRadius: "20px",
    padding: "6px 12px",
    fontSize: "12px",
    fontWeight: "500",
    transition: "all 0.3s ease",
    minWidth: "auto",
    whiteSpace: "nowrap",
    marginRight: "4px",
    marginBottom: "6px",
  });

  handleButtonHover = (e, activeSort, isEntering) => {
    if (this.state.sortBy !== activeSort) {
      if (isEntering) {
        e.target.style.backgroundColor = "#f0f8e8";
        e.target.style.borderColor = "#88c000";
        e.target.style.color = "#88c000";
      } else {
        e.target.style.backgroundColor = "white";
        e.target.style.borderColor = "#ddd";
        e.target.style.color = "#666";
      }
    }
  };

  render() {
    const { products, loading, error, searchTerm, sortBy } = this.state;
    const { setting } = this.props;

    // Get filtered and sorted products
    const displayProducts = this.getFilteredAndSortedProducts();
    const totalProducts = products ? products.length : 0;

    // SEO data for deals page
    const dealsSEO = {
      title: generatePageTitle('deals', { title: 'Fırsat Ürünler' }, setting && setting.setting && setting.setting.title),
      description: generateMetaDescription('deals', { title: 'Fırsat Ürünler' }, setting && setting.setting && setting.setting.title),
      keywords: generateKeywords('deals', { title: 'Fırsat Ürünler' }, ['fırsat', 'indirim', 'kampanya', 'özel fiyat']),
      type: 'website',
      url: '/deals'
    };

    return (
      <React.Fragment>
        <SEO {...dealsSEO} />
        <PageTitle title="Fırsat Ürünler" />

        <div className="container-fluid" style={{ paddingLeft: '15px', paddingRight: '15px' }}>
          {/* Full Width Filter Section */}
          <div className="row" style={{ marginTop: 20 }}>
            <div className="col-12">
              {/* Search and Sort Controls */}
              <div
                style={{
                  background: "linear-gradient(135deg, #f0f8e8 0%, #fff 100%)",
                  border: "1px solid #e0e0e0",
                  borderRadius: "12px",
                  padding: "20px 15px",
                  marginBottom: 10,
                }}
              >
                {/* Search and Filter Section */}
                <div className="row">
                  <div className="col-lg-4 col-md-5 col-sm-12 mb-3">
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span
                          className="input-group-text"
                          style={{
                            backgroundColor: "#88c000",
                            color: "white",
                            border: "none",
                          }}
                        >
                          <FontAwesomeIcon icon={["fas", "search"]} />
                        </span>
                      </div>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Ne aramıştınız?"
                        value={searchTerm}
                        onChange={this.handleSearchChange}
                        style={{
                          border: "1px solid #88c000",
                          borderLeft: "none",
                          fontSize: "14px",
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-lg-8 col-md-7 col-sm-12">
                    <div className="d-flex flex-wrap align-items-center justify-content-lg-end">
                      <button
                        className="btn btn-sm"
                        onClick={() => this.handleSortChange("featured")}
                        style={this.getButtonStyle("featured")}
                        onMouseEnter={(e) =>
                          this.handleButtonHover(e, "featured", true)
                        }
                        onMouseLeave={(e) =>
                          this.handleButtonHover(e, "featured", false)
                        }
                      >
                        <FontAwesomeIcon
                          icon={["fas", "star"]}
                          style={{ marginRight: "5px", fontSize: "11px" }}
                        />
                        Öne çıkan
                      </button>

                      <button
                        className="btn btn-sm"
                        onClick={() => this.handleSortChange("new")}
                        style={this.getButtonStyle("new")}
                        onMouseEnter={(e) =>
                          this.handleButtonHover(e, "new", true)
                        }
                        onMouseLeave={(e) =>
                          this.handleButtonHover(e, "new", false)
                        }
                      >
                        <FontAwesomeIcon
                          icon={["fas", "tag"]}
                          style={{ marginRight: "5px", fontSize: "11px" }}
                        />
                        Yeni ürün
                      </button>

                      <button
                        className="btn btn-sm"
                        onClick={() => this.handleSortChange("discounted")}
                        style={this.getButtonStyle("discounted")}
                        onMouseEnter={(e) =>
                          this.handleButtonHover(e, "discounted", true)
                        }
                        onMouseLeave={(e) =>
                          this.handleButtonHover(e, "discounted", false)
                        }
                      >
                        <FontAwesomeIcon
                          icon={["fas", "percent"]}
                          style={{ marginRight: "5px", fontSize: "11px" }}
                        />
                        İndirimli ürünler
                      </button>

                      <button
                        className="btn btn-sm"
                        onClick={() => this.handleSortChange("price_asc")}
                        style={this.getButtonStyle("price_asc")}
                        onMouseEnter={(e) =>
                          this.handleButtonHover(e, "price_asc", true)
                        }
                        onMouseLeave={(e) =>
                          this.handleButtonHover(e, "price_asc", false)
                        }
                      >
                        <FontAwesomeIcon
                          icon={["fas", "sort-amount-up"]}
                          style={{ marginRight: "5px", fontSize: "11px" }}
                        />
                        Fiyat artan
                      </button>

                      <button
                        className="btn btn-sm"
                        onClick={() => this.handleSortChange("price_desc")}
                        style={this.getButtonStyle("price_desc")}
                        onMouseEnter={(e) =>
                          this.handleButtonHover(e, "price_desc", true)
                        }
                        onMouseLeave={(e) =>
                          this.handleButtonHover(e, "price_desc", false)
                        }
                      >
                        <FontAwesomeIcon
                          icon={["fas", "sort-amount-down"]}
                          style={{ marginRight: "5px", fontSize: "11px" }}
                        />
                        Fiyat azalan
                      </button>

                      <button
                        className="btn btn-sm"
                        onClick={() => this.handleSortChange("newest")}
                        style={this.getButtonStyle("newest")}
                        onMouseEnter={(e) =>
                          this.handleButtonHover(e, "newest", true)
                        }
                        onMouseLeave={(e) =>
                          this.handleButtonHover(e, "newest", false)
                        }
                      >
                        <FontAwesomeIcon
                          icon={["fas", "clock"]}
                          style={{ marginRight: "5px", fontSize: "11px" }}
                        />
                        Yeni eklenenler
                      </button>

                      {(searchTerm || sortBy !== "default") && (
                        <button
                          className="btn btn-sm"
                          onClick={() => {
                            this.setState({
                              searchTerm: "",
                              sortBy: "default",
                            });
                          }}
                          style={{
                            backgroundColor: "#ff6b6b",
                            color: "white",
                            border: "1px solid #ff6b6b",
                            borderRadius: "20px",
                            padding: "6px 12px",
                            fontSize: "12px",
                            fontWeight: "500",
                            transition: "all 0.3s ease",
                            minWidth: "auto",
                            whiteSpace: "nowrap",
                            marginRight: "4px",
                            marginBottom: "6px",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = "#ff5252";
                            e.target.style.borderColor = "#ff5252";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = "#ff6b6b";
                            e.target.style.borderColor = "#ff6b6b";
                          }}
                        >
                          <FontAwesomeIcon
                            icon={["fas", "times"]}
                            style={{ marginRight: "5px", fontSize: "11px" }}
                          />
                          Temizle
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Area with Sidebar */}
          <div className="row">
            {/* Left Sidebar */}
            <div className="col-md-3">
              <CartWidget />
              <CategoriesWidget />
            </div>

            {/* Products Grid */}
            <div className="col-md-9">
              <div className="row">
                {loading ? (
                  <div className="col-12 text-center py-5">
                    <Loading />
                  </div>
                ) : error ? (
                  <div className="col-12 text-center py-5">
                    <div className="alert alert-danger">
                      <FontAwesomeIcon
                        icon={["fas", "exclamation-triangle"]}
                        style={{
                          fontSize: '48px',
                          color: '#6c757d',
                          marginBottom: '20px'
                        }}
                      />
                      <h5>Bir hata oluştu</h5>
                      <p>Fırsat ürünleri yüklenirken bir sorun yaşandı.</p>
                    </div>
                  </div>
                ) : displayProducts.length > 0 ? (
                  displayProducts.map((product, i) => (
                    <div
                      className="col-xl-3 col-lg-4 col-md-6 col-sm-6 mb-4"
                      key={product._id || i}
                    >
                      <VibrantProductCard {...product} />
                    </div>
                  ))
                ) : (
                  <div className="col-12 text-center py-5">
                    <div className="alert alert-info">
                      <h5>Ürün Bulunamadı</h5>
                      <p>
                        Aradığınız kriterlere uygun ürün bulunamadı. Lütfen
                        filtreleri değiştirip tekrar deneyin.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Results Count */}
              {!loading && !error && displayProducts.length > 0 && (
                <div className="row mt-4">
                  <div className="col-12">
                    <div className="alert alert-light text-center">
                      <strong>{displayProducts.length}</strong> fırsat ürünü
                      gösteriliyor
                      {totalProducts !== displayProducts.length && (
                        <span> (toplam {totalProducts} ürün içinden)</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

const mapStateToProps = ({ setting }) => {
  return { setting };
};

export default connect(mapStateToProps)(DealProducts);


// WEBPACK FOOTER //
// ./src/routers/DealProducts/index.js